<script setup lang="ts">
import v from 'voca'
import type { Server } from '~~/models/server'

useTitle('servers.title', { t: true })

const config = useRuntimeConfig()
const { t } = useI18n()
const { region } = useRegion()

interface State {
  search: string
  filters: string[]
}

const state: State = reactive({
  search: '',
  filters: []
})

const apiClient = useAPIClient()
const { servers, error } = await apiClient.servers()
if (error.value) throw error.value


const filtered = computed(() => {
  if (!servers.value) return []

  let results = servers.value

  if (state.search) {
    results = results.filter(s => {
      return v.includes(v.lowerCase(s.name), v.lowerCase(state.search))
    })
  }

  if (state.filters.length) {    
    results = results.filter(s => {
      return state.filters.some(f => s.tags?.includes(f))
    })
  }

  return results
})

function isFilterOn(name: string) {
  return state.filters.includes(name)
}

function applyTag(name: string) {
  if (name === 'all') {
    state.filters = []
  } else {
    state.filters = [name]
  }
}

function onlineTagText(s: Server) {
  return s.online ? t('servers.online') : t('servers.offline')
}

function onlineTagClass(s: Server) {
  return s.online ? 'success' : 'error'
}

function loadTagText(s: Server) {
  let load = 'low'
  if (s.load >= 30) { 
    load = 'medium'
  } else if (s.load >= 65) {
    load = 'high'
  }
  return t(`servers.load_labels.${load}`)
}

function loadTagClass(s: Server) {
  let klass = 'success'
  if (s.load >= 30) { 
    klass = 'warning'
  } else if (s.load >= 65) {
    klass = 'error'
  }
  return klass
}
</script>

<template>
  <div class="servers-page page">
    <header>
      <page-header :heading="$t('servers.title')" />

      <div class="content-limit">
        <FormKit
          type="search"
          :placeholder="$t('common.search')"
          v-model="state.search"
          input-class="styled"
        />

        <div class="filters">
          <button
            class="tag" :class="{ selected: !state.filters.length }"
            @click="applyTag('all')"
          >
            {{ $t('servers.all') }}
          </button>
          <button
            class="tag emphasis" :class="{ selected: isFilterOn('premium') }"
            @click="applyTag('premium')"
          >
            {{ $t('plans.premium') }}
          </button>
          <button
            v-if="region === 'china'" class="tag success"
            :class="{ selected: isFilterOn('optimized') }"
            @click="applyTag('optimized')"
          >
            {{ $t('servers.optimized') }}
          </button>
        </div>
      </div>
    </header>

    <div>
      <table class="servers-table styled">
        <thead>
          <tr>
            <th width>{{ $t('servers.server') }}</th>
            <th>{{ $t('servers.country') }}</th>
            <th>{{ $t('servers.status') }}</th>
            <th>{{ $t('servers.load') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="server of filtered" :key="server.id">
            <td>
              <div class="name">
                {{ server.name }}
                <div v-if="server.tier === 2" class="small emphasis tag">
                  {{ $t('plans.premium') }}
                </div>

                <div
                  v-if="server.tags?.includes('optimized')"
                  class="small success tag"
                >
                  {{ $t('servers.optimized') }}
                </div>
              </div>
            </td>
            <td class="country">
              <img
                :src="`${config.public.cdnUrl}/assets/images/countries/${server.city.countryCode}.svg`"
                width="24"
              />
              {{ server.city.country }}
            </td>
            <td>
              <div class="tag" :class="onlineTagClass(server)">
                {{ onlineTagText(server) }}
              </div>
            </td>
            <td>
              <div class="tag" :class="loadTagClass(server)">
                {{ loadTagText(server) }}
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped lang="scss">
.servers-page {
  background: $color-aqua;
  color: $color-white;

  header {
    text-align: center;
    margin-bottom: 2rem;
  }

  .filters {
    display: inline-flex;
    gap: 0.5rem;
    margin-top: 1.5rem;
  }
}

.servers-table {
  tr { border-color: rgba($color-white, 0.4); }

  .name {
    display: inline-flex;
    align-items: center;

    .tag { margin-left: 0.5rem; }
  }

  .country {
    display: flex;
    align-items: center;

    img { margin-right: 0.5rem; }
  }
}
</style>
